import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, ArrowLeft, ChevronDown, ChevronUp, RotateCcw, Package, Truck, FileText, Image, Download, Sparkles } from 'lucide-react';
import { log } from '../lib/logger';
import { PaymentCapturedBadge } from '../components/PaymentCapturedBadge';
import { DocumentsHub } from '../components/DocumentsHub';
import { ProofThumbnail } from '../components/ProofThumbnail';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/enhanced-orders/my-orders`, {
      });
      // Filter out DRAFT orders
      const completedOrders = response.data.filter(order => order.status !== 'draft');
      setOrders(completedOrders);
    } catch (error) {
      log.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (orderId, asDraft = false) => {
    const prompt = asDraft
      ? 'Create a NEW DRAFT copy of this order that you can edit before submitting?'
      : 'Create a new order with the same items? This order will go directly into production.';
    if (!window.confirm(prompt)) {
      return;
    }

    setReorderLoading(orderId);
    try {
      const response = await axios.post(
        `${API_URL}/api/enhanced-orders/${orderId}/reorder${asDraft ? '?as_draft=true' : ''}`,
        {},

      );

      if (asDraft) {
        navigate('/order-summary', { state: { orderId: response.data.order_id } });
      } else {
        alert(`Reorder successful! New order number: ${response.data.order_number}`);
        fetchOrders(); // Refresh the list
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reorder. Please try again.');
    } finally {
      setReorderLoading(null);
    }
  };

  const handleDownloadProof = async (orderId, orderNumber) => {
    // Pull the proof as a blob with the auth token attached, then
    // synthesize an <a download> click to trigger the browser's save
    // dialog. This avoids needing to forward cookies/tokens through a
    // plain anchor href (which would open a new tab + error on 401).
    try {
      const res = await axios.get(
        `${API_URL}/api/enhanced-orders/${orderId}/proof-pdf`,
        { responseType: 'blob' },
      );
      // eslint-disable-next-line no-undef
      const url = URL.createObjectURL(res.data);
      // eslint-disable-next-line no-undef
      const a = document.createElement('a');
      a.href = url;
      a.download = `proof-${orderNumber || orderId}.pdf`;
      // eslint-disable-next-line no-undef
      document.body.appendChild(a);
      a.click();
      a.remove();
      // eslint-disable-next-line no-undef
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (error) {
      const status = error?.response?.status;
      // Blob error responses need to be read as text before we can see the detail
      let message = 'Failed to download proof PDF.';
      if (status === 400) {
        message = 'No artwork was attached to this order, so no proof is available to download.';
      } else if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed?.detail || message;
        } catch (parseErr) {
          // Blob wasn't JSON — fall through to the default message.
          // Captured in dev so we can spot backend format drift.
          log.debug('[POHistory] Could not parse error blob:', parseErr?.name || parseErr);
        }
      }
      alert(message);
    }
  };

  // iter114s.79 — Cut-file SVG download removed from customer-facing
  // POHistory per operator request; the artefact is no longer a
  // customer deliverable. The backend route /enhanced-orders/{id}/cut-file
  // remains intact for internal/production tooling.


  // Generic helper for the document-download triplet (proof, quote,
  // invoice). Each handler differs only in path/filename + which 4xx
  // detail to surface, so we factor the boilerplate (blob fetch,
  // synthesised <a download>, error-blob JSON parsing, cleanup) and
  // keep the per-doc fns at the call site for clarity.
  const downloadDocPdf = async ({ orderId, path, filename, fallbackMessage }) => {
    try {
      const res = await axios.get(`${API_URL}${path}`, { responseType: 'blob' });
      // eslint-disable-next-line no-undef
      const url = URL.createObjectURL(res.data);
      // eslint-disable-next-line no-undef
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      // eslint-disable-next-line no-undef
      document.body.appendChild(a);
      a.click();
      a.remove();
      // eslint-disable-next-line no-undef
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (error) {
      let message = fallbackMessage;
      if (error?.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await error.response.data.text())?.detail || message;
        } catch (parseErr) {
          log.debug('[POHistory] Doc download error blob not JSON:', parseErr?.name || parseErr);
        }
      }
      alert(message);
    }
  };

  const handleDownloadQuote = (orderId, orderNumber) =>
    downloadDocPdf({
      orderId,
      path: `/api/enhanced-orders/${orderId}/quote-pdf`,
      filename: `quote-${orderNumber || orderId}.pdf`,
      fallbackMessage: 'Failed to download quote PDF.',
    });

  const handleDownloadOrderConfirmation = (orderId, orderNumber) =>
    downloadDocPdf({
      orderId,
      // /invoice-pdf is the legacy URL — keeps in-flight bookmarks
      // working — but the rendered document is now branded as "ORDER
      // CONFIRMATION" per iter108. /order-confirmation-pdf is the
      // new canonical path; the alias was added on the backend.
      path: `/api/enhanced-orders/${orderId}/order-confirmation-pdf`,
      filename: `order-confirmation-${orderNumber || orderId}.pdf`,
      fallbackMessage: 'Failed to download order confirmation PDF.',
    });



  const handleDownloadMockup = async (orderId, cacheKey, orderNumber, index) => {
    // Clean mockup endpoint is auth-gated (and checks the caller owns the
    // order) — fetch with the Axios instance (which carries the JWT) and
    // trigger a client-side blob download so the file lands with the
    // server-supplied Content-Disposition filename.
    try {
      const resp = await axios.get(
        `${API_URL}/api/product-preview/image/${cacheKey}/clean`,
        { params: { order_id: orderId }, responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `EG3-mockup-${orderNumber}-${index + 1}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      let message = 'Could not download mockup. Please try again.';
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed?.detail || message;
        } catch (parseErr) {
          log.debug('[POHistory] mockup error blob parse failed:', parseErr?.name);
        }
      }
      alert(message);
    }
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const canReorder = (status) => {
    // Keep in sync with reorderable_statuses in routes/enhanced_orders.py
    return [
      'completed', 'delivered', 'shipped', 'proof_approved',
      'in_production', 'paid', 'proof_sent', 'awaiting_approval',
    ].includes(status);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <header className="bg-[#4A7C59] text-white py-6 px-6 shadow-md">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/splash')}
              className="flex items-center gap-2 text-white hover:text-green-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
            <h1 className="text-3xl font-bold">Purchase Order Request History</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <p className="text-gray-600 mb-8">
            If you have any questions or need to inquire about a status update on a particular order please reach out to your designated
            account rep or email us at <a href="mailto:jfishburn@eg3.ca" className="text-[#4A7C59] hover:underline">jfishburn@eg3.ca</a> with order details.
          </p>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6FBF73] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No orders found</p>
              <p className="text-gray-500 mt-2">Your order history will appear here once you place your first order.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-4 px-3 font-semibold text-gray-700 w-20">Preview</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Order Number</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Items</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Total</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Status</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Date</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <React.Fragment key={order.id}>
                      <tr className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-3">
                          {/* iter114s.74 — at-a-glance artwork+dieline
                              thumbnail. Lazy-loads on scroll so we
                              don't render-bomb the artwork rasteriser
                              on long PO histories. Clicking it
                              expands the row, same as the order
                              number column. */}
                          <button
                            type="button"
                            onClick={() => toggleOrderDetails(order.id)}
                            className="block rounded-md focus:outline-none focus:ring-2 focus:ring-[#6FBF73]/60 hover:opacity-90 transition"
                            aria-label={`Open details for order ${order.order_number}`}
                            data-testid={`po-row-thumbnail-${order.id}`}
                          >
                            <ProofThumbnail
                              orderId={order.id}
                              size={56}
                              rounded="md"
                              testId={`po-row-thumbnail-img-${order.id}`}
                              alt={`Proof preview for ${order.order_number}`}
                            />
                          </button>
                        </td>
                        <td className="py-4 px-4 text-gray-800 font-medium">
                          <button
                            onClick={() => toggleOrderDetails(order.id)}
                            className="flex items-center gap-2 text-[#4A7C59] hover:text-[#6FBF73] transition-colors"
                          >
                            {order.order_number}
                            {expandedOrderId === order.id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        </td>
                        <td className="py-4 px-4 text-gray-800">{order.items?.length || 0}</td>
                        <td className="py-4 px-4 text-gray-800">
                          {order.currency} ${(order.total_price || 0).toFixed(2)}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              order.status === 'completed' || order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                              order.status === 'proof_approved' || order.status === 'in_production' ? 'bg-blue-100 text-blue-800' :
                              order.status === 'proof_pending' ? 'bg-yellow-100 text-yellow-800' :
                              order.status === 'proof_rejected' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {order.status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
                            </span>
                            {order.docs_error && (
                              <span
                                className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 font-semibold cursor-help"
                                title={`Proof auto-generation failed: ${order.docs_error}. Your account manager will regenerate it manually.`}
                                data-testid={`po-history-docs-error-badge-${order.id}`}
                              >
                                ⚠ proof failed
                              </span>
                            )}
                            <PaymentCapturedBadge
                              paymentStatus={order.payment_status}
                              capturedAt={order.payment_captured_at}
                              testId={`po-history-payment-captured-${order.id}`}
                            />
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-600">{formatDate(order.created_at)}</td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2">
                            {order.status === 'proof_pending' && (
                              <button
                                onClick={() => navigate(`/proof-approval/${order.id}`)}
                                className="text-[#4A7C59] hover:underline font-medium cursor-pointer bg-transparent border-none"
                              >
                                Review Proof
                              </button>
                            )}
                            {canReorder(order.status) && (
                              <>
                                <button
                                  onClick={() => handleReorder(order.id, false)}
                                  disabled={reorderLoading === order.id}
                                  data-testid={`reorder-btn-${order.id}`}
                                  className="flex items-center gap-1 px-3 py-1 bg-[#4A7C59] text-white rounded hover:bg-[#6FBF73] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                  <RotateCcw className="w-3 h-3" />
                                  {reorderLoading === order.id ? 'Processing...' : 'Reorder'}
                                </button>
                                <button
                                  onClick={() => handleReorder(order.id, true)}
                                  disabled={reorderLoading === order.id}
                                  data-testid={`edit-reorder-btn-${order.id}`}
                                  className="flex items-center gap-1 px-3 py-1 bg-white border border-[#4A7C59] text-[#4A7C59] rounded hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                  title="Clone as draft — edit before submitting"
                                >
                                  Edit &amp; Reorder
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expandable Details Row */}
                      {expandedOrderId === order.id && (
                        <tr className="bg-green-50">
                          <td colSpan="7" className="py-6 px-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Items Section */}
                              <div className="bg-white p-4 rounded-lg shadow-sm">
                                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                  <Package className="w-5 h-5 text-[#4A7C59]" />
                                  Order Items
                                </h3>
                                <div className="space-y-2">
                                  {order.items?.map((item, idx) => (
                                    <div key={idx} className="border-b border-gray-200 pb-2 last:border-0">
                                      <p className="font-medium text-gray-700">{item.product_name}</p>
                                      <div className="text-sm text-gray-600 mt-1">
                                        <p>Format: {item.format}</p>
                                        {item.material && <p>Material: {item.material}</p>}
                                        {item.size && <p>Size: {item.size}</p>}
                                        <p>Quantity: {item.quantity}</p>
                                        <p className="font-semibold text-[#4A7C59] mt-1">
                                          ${item.price?.toFixed(2) || '0.00'}
                                        </p>
                                      </div>
                                    </div>
                                  )) || <p className="text-gray-500">No items</p>}
                                </div>
                              </div>

                              {/* Shipping Information */}
                              <div className="bg-white p-4 rounded-lg shadow-sm">
                                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                  <Truck className="w-5 h-5 text-[#4A7C59]" />
                                  Shipping Information
                                </h3>
                                {order.shipping_info ? (
                                  <div className="text-sm text-gray-600 space-y-1">
                                    <p className="font-medium">{order.shipping_info.company_name}</p>
                                    <p>{order.shipping_info.contact_name}</p>
                                    <p>{order.shipping_info.address}</p>
                                    <p>{order.shipping_info.city}, {order.shipping_info.province} {order.shipping_info.postal_code}</p>
                                    <p>{order.shipping_info.country}</p>
                                    {order.shipping_info.phone && <p>Phone: {order.shipping_info.phone}</p>}
                                    {order.shipping_info.email && <p>Email: {order.shipping_info.email}</p>}
                                  </div>
                                ) : (
                                  <p className="text-gray-500">No shipping information</p>
                                )}
                              </div>

                              {/* Artwork & Proof Info */}
                              <div className="bg-white p-4 rounded-lg shadow-sm">
                                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                  <Image className="w-5 h-5 text-[#4A7C59]" />
                                  Artwork & Proof
                                </h3>
                                {/* iter114s.74 — large artwork+dieline
                                    preview alongside the file count.
                                    Same renderer as the row thumbnail
                                    (cache hit when both are visible). */}
                                <div className="flex gap-4 mb-3">
                                  <ProofThumbnail
                                    orderId={order.id}
                                    size={140}
                                    rounded="lg"
                                    testId={`po-expanded-thumbnail-${order.id}`}
                                    alt={`Proof preview for ${order.order_number}`}
                                  />
                                  <div className="text-sm text-gray-600 space-y-1 flex-1 min-w-0">
                                    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                                      Artwork + Die line preview
                                    </p>
                                    <p>Artwork files: {order.artwork_files?.length || 0}</p>
                                    {order.proof_file_id && (
                                      <p className="text-green-600 font-medium">✓ Proof uploaded</p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-sm text-gray-600 space-y-2">
                                  {order.artwork_instructions && (
                                    <div className="mt-2">
                                      <p className="font-medium text-gray-700">Instructions:</p>
                                      <p className="text-gray-600 italic">{order.artwork_instructions}</p>
                                    </div>
                                  )}

                                  {/* Live Mockup downloads — one clean
                                      (watermark-free) PNG per cache_key the
                                      customer generated during the quote. */}
                                  {(order.mockup_cache_keys || []).length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                      <p className="font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                        <Sparkles className="w-4 h-4 text-amber-500" />
                                        Live Mockups — watermark-free
                                      </p>
                                      <div className="flex flex-wrap gap-2">
                                        {order.mockup_cache_keys.map((ck, i) => (
                                          <button
                                            key={ck}
                                            type="button"
                                            onClick={() => handleDownloadMockup(order.id, ck, order.order_number, i)}
                                            data-testid={`download-mockup-${order.id}-${i}`}
                                            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100 font-medium"
                                          >
                                            <Download className="w-3.5 h-3.5" />
                                            Mockup {i + 1}
                                          </button>
                                        ))}
                                      </div>
                                      <p className="text-[11px] text-gray-500 mt-1.5">
                                        Clean PNGs are tied to this Order ID — save them to your brand library.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* iter111: consolidated Documents hub —
                                  every PDF/SVG artefact for this order
                                  in one place, with consistent labeling
                                  + accent colours. Visibility rules
                                  live inside the component. */}
                              <DocumentsHub
                                order={order}
                                onDownloadProof={handleDownloadProof}
                                onDownloadQuote={handleDownloadQuote}
                                onDownloadOrderConfirmation={handleDownloadOrderConfirmation}
                              />

                              {/* Tracking & Notes */}
                              <div className="bg-white p-4 rounded-lg shadow-sm">
                                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                  <FileText className="w-5 h-5 text-[#4A7C59]" />
                                  Delivery & Notes
                                </h3>
                                <div className="text-sm text-gray-600 space-y-2">
                                  {order.tracking_number && (
                                    <div>
                                      <p className="font-medium text-gray-700">Tracking Number:</p>
                                      <p className="font-mono bg-gray-100 px-2 py-1 rounded mt-1">
                                        {order.tracking_number}
                                      </p>
                                    </div>
                                  )}
                                  {order.expected_ship_date && (
                                    <div>
                                      <p className="font-medium text-gray-700">Expected Ship Date:</p>
                                      <p>{formatDate(order.expected_ship_date)}</p>
                                    </div>
                                  )}
                                  {order.proof_rejection_notes && (
                                    <div>
                                      <p className="font-medium text-red-600">Rejection Notes:</p>
                                      <p className="text-gray-600 italic">{order.proof_rejection_notes}</p>
                                    </div>
                                  )}
                                  {order.docs_error && (
                                    <div data-testid={`po-history-docs-error-detail-${order.id}`}>
                                      <p className="font-medium text-red-600">
                                        ⚠ Proof generation failed
                                      </p>
                                      <p className="text-gray-600 italic">
                                        {order.docs_error}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        Don&apos;t worry — your account manager will regenerate the proof manually and email it to you shortly.
                                      </p>
                                    </div>
                                  )}
                                  {!order.tracking_number && !order.expected_ship_date && !order.proof_rejection_notes && !order.docs_error && (
                                    <p className="text-gray-500">No additional information</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default POHistory;
